import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useApp } from '../../context/AppContext';

const JokesDisplay = () => {
    const navigate = useNavigate();
    const { showToast } = useApp();
    const [jokes, setJokes] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchJokes();
    }, []);

    const fetchJokes = async () => {
        setLoading(true);
        try {
            // Fetch from BOTH sources
            const [articlesRes, jokesRes] = await Promise.all([
                api.get('/articles?category=Jokes&status=live&limit=3'),
                api.get('/jokes?status=published&limit=3')
            ]);

            // ✅ Handle different response structures for articles
            let articlesData = articlesRes.data;
            if (articlesRes.data.articles) {
                articlesData = articlesRes.data.articles;
            }
            if (!Array.isArray(articlesData)) {
                articlesData = [];
            }

            // Process articles
            const articleJokes = articlesData.map(article => ({
                _id: article._id,
                slug: article.slug,
                source: 'article',
                title: article.title,
                text: article.excerpt || article.content?.replace(/<[^>]*>/g, '').substring(0, 100),
                image: article.img,
                imageData: article.imgData,
                views: article.views || 0,
                date: article.date || article.createdAt
            }));

            // ✅ Handle different response structures for jokes
            let jokesData = jokesRes.data;
            if (jokesRes.data.jokes) {
                jokesData = jokesRes.data.jokes;
            }
            if (!Array.isArray(jokesData)) {
                jokesData = [];
            }

            // Process manager jokes
            const managerJokes = jokesData.map(joke => ({
                _id: joke._id,
                slug: joke.slug,
                source: 'manager',
                title: joke.title || 'Funny Joke',
                text: joke.text,
                image: joke.image,
                imageData: joke.imageData,
                views: joke.views || 0,
                date: joke.createdAt
            }));

            // Combine and take first 3
            const combined = [...articleJokes, ...managerJokes].slice(0, 3);
            setJokes(combined);
        } catch (error) {
            console.error('Error fetching jokes:', error);
            setJokes([]);
        } finally {
            setLoading(false);
        }
    };

    const stripHtml = (html) => {
        if (!html) return '';
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || '';
    };

    if (loading) {
        return (
            <div className="loader">
                <div className="loader-spinner"></div>
            </div>
        );
    }

    if (jokes.length === 0) {
        return null;
    }

    return (
        <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '24px',
            margin: '28px 0',
            color: '#fff',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span>😄</span> Jokes Corner
                    </h2>
                    <p style={{ margin: '5px 0 0', opacity: 0.9, fontSize: '13px' }}>Get your daily dose of laughter!</p>
                </div>
                <button
                    onClick={() => navigate('/jokes')}
                    style={{
                        padding: '8px 15px',
                        borderRadius: '20px',
                        border: 'none',
                        background: 'rgba(255,255,255,0.2)',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '13px'
                    }}
                >
                    View All Jokes →
                </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                {jokes.map((joke) => (
                    <div
                        key={joke._id}
                        onClick={() => {
                            if (joke.source === 'article') {
                                navigate(joke.slug ? `/article/${joke.slug}` : `/article/${joke._id}`);
                            } else {
                                navigate(joke.slug ? `/joke/${joke.slug}` : `/joke/${joke._id}`);
                            }
                        }}
                        style={{
                            background: 'rgba(255,255,255,0.15)',
                            borderRadius: '12px',
                            padding: '20px',
                            transition: 'transform 0.3s',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                        }}
                    >
                        {joke.image || joke.imageData ? (
                            <div style={{ height: '160px', marginBottom: '15px', borderRadius: '8px', overflow: 'hidden' }}>
                                <img
                                    src={joke.image || joke.imageData}
                                    alt={joke.title}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                            </div>
                        ) : (
                            <div style={{ fontSize: '40px', marginBottom: '10px' }}>😂</div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                            <span style={{
                                fontSize: '10px',
                                background: 'rgba(0,0,0,0.3)',
                                padding: '2px 8px',
                                borderRadius: '10px'
                            }}>
                                {joke.source === 'article' ? '📰 Article' : '😂 Joke'}
                            </span>
                            <span style={{ fontSize: '11px', opacity: 0.7 }}>
                                👁️ {joke.views || 0}
                            </span>
                        </div>
                        <h3 style={{ fontSize: '16px', marginBottom: '8px', fontWeight: 'bold' }}>
                            {joke.title}
                        </h3>
                        <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '15px' }}>
                            {stripHtml(joke.text || '').substring(0, 100)}...
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default JokesDisplay;